import React from 'react';

import {
  errorFieldsTypes,
  ErrorsFields,
  FieldsHelpText,
  FormState,
  MultiOptionValue,
  prefixTypes,
  RequiredFields,
  TakeActionFormState,
} from '@/core/interfaces/common';

import { handleNumberFormat } from '@/utils/helpers';

import { ContactFields } from '../ContactFields';

export interface CustomFieldErrors {
  value: Array<string>;
}

export interface ContactFieldsContainerProps {
  backgroundColor: string;
  error: ErrorsFields;
  formState: FormState | TakeActionFormState;
  handleBlurAddress?: (streetAddress?: string, zipCode?: string) => void;
  requiredFields?: RequiredFields;
  setError: React.Dispatch<React.SetStateAction<ErrorsFields>>;
  setFormState: React.Dispatch<React.SetStateAction<FormState | TakeActionFormState>>;
  textColor: string;
  CustomContactFieldsComponent?: React.ComponentType<any>;
  helpText?: FieldsHelpText;
  ariaLabels?: {
    prefix?: string;
    firstName?: string;
    lastName?: string;
    streetAddress?: string;
    zipCode?: string;
    email?: string;
    phoneNumber?: string;
    customFields?: { [uuid: string]: string };
  };
}

export const ContactFieldsContainer = ({
  backgroundColor,
  error,
  formState,
  handleBlurAddress,
  requiredFields,
  setError,
  setFormState,
  textColor,
  CustomContactFieldsComponent,
  helpText,
  ariaLabels,
}: ContactFieldsContainerProps) => {
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      prefix: value as prefixTypes,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: {
        checked,
        name,
        type,
        value,
      },
    } = event;

    const inputValue = type === 'checkbox' ? checked : value;

    setFormState(prevValues => ({
      ...prevValues,
      [name]: inputValue,
    }));
  };

  const handleCustomFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number
  ) => {
    const customFieldsData = [...formState.customFields];

    customFieldsData[index] = {
      ...customFieldsData[index],
      value: event.target.value,
    };

    setFormState(prevValues => ({
      ...prevValues,
      customFields: customFieldsData,
    }));
  };

  const handleMultiCustomFieldChange = (
    values: Array<MultiOptionValue>,
    index: number
  ) => {
    const customFieldsData = [...formState.customFields];

    customFieldsData[index] = {
      ...customFieldsData[index],
      values,
    };

    setFormState(prevValues => ({
      ...prevValues,
      customFields: customFieldsData,
    }));
  };

  const handlePhoneInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      phoneNumber: handleNumberFormat(value) || '',
    }));
  };

  const handleChangeAddress = (value: string) => {
    setFormState(prevValues => ({
      ...prevValues,
      streetAddress: value,
    }));
  };

  const handleSetZipCode = (value: string) => {
    setFormState(prevValues => ({
      ...prevValues,
      zipCode: value,
    }));

    if (value) {
      handleClearFieldError('zipCode');
    }
  };

  const handleClearFieldError = (fieldName: string) => {
    setError(prevValues => ({
      ...prevValues,
      [fieldName]: [],
    }));
  };

  const handleSetFieldError = (field: errorFieldsTypes, message: string) => {
    setError(prev => ({
      ...prev,
      [field]: [message],
    }));
  };

  const handleSetCustomFieldError = (index: number, message: string) => {
    setError(prev => {
      const customFields = [...(prev.customFields ?? [])];

      while (customFields.length <= index) {
        customFields.push({
          value: [],
        });
      }

      customFields[index] = {
        value: [message],
      };

      return {
        ...prev,
        customFields,
      };
    });
  };

  const handleClearCustomFieldError = (index: number) => {
    if (error?.customFields) {
      const customFieldsData = [...error.customFields];

      customFieldsData[index] = {
        value: [],
      };

      setError(prevValues => ({
        ...prevValues,
        customFields: customFieldsData,
      }));
    }
  };

  const renderContactError = (errorField: errorFieldsTypes): string => {
    if (error && !!error[errorField] && !!error[errorField]?.length) {
      const errorArray = error[errorField];

      if (errorArray) {
        return errorArray[0];
      }
    }

    return '';
  };

  const contactFieldsProps = {
    ariaLabels,
    backgroundColor,
    customFields: formState.customFields,
    email: formState.email,
    error,
    firstName: formState.firstName,
    handleBlurAddress,
    handleChangeAddress,
    handleClearCustomFieldError,
    handleClearFieldError,
    handleCustomFieldChange,
    handleInputChange,
    handleMultiCustomFieldChange,
    handlePhoneInputChange,
    handleSelectChange,
    handleSetZipCode,
    lastName: formState.lastName,
    phoneNumber: formState.phoneNumber,
    prefix: formState.prefix,
    renderContactError,
    requiredFields,
    streetAddress: formState.streetAddress,
    zipCode: formState.zipCode,
  };

  return (
    <>
      {CustomContactFieldsComponent ? (
        <CustomContactFieldsComponent
          {...contactFieldsProps}
          handleSetCustomFieldError={handleSetCustomFieldError}
          handleSetFieldError={handleSetFieldError}
          helpText={helpText}
        />
      ) : (
        // Field help is intentionally not passed to the legacy fields
        // used by pre-V4 forms.
        <ContactFields {...contactFieldsProps} />
      )}
    </>
  );
};

ContactFieldsContainer.defaultProps = {
  handleBlurAddress: () => null,
};

